import type React from 'react';
import { forwardRef, type HTMLAttributes } from 'react';

import { cva } from 'cva';

import styles from './chip.module.css';

export const chipVariants = cva({
  base: styles.chip,
  variants: {
    size: {
      xs: styles.size_xs,
      s: styles.size_s,
      m: styles.size_m,
      l: styles.size_l,
    },
    color: {
      default: styles.color_default,
      teal: styles.color_teal,
      green: styles.color_green,
      pink: styles.color_pink,
      yellow: styles.color_yellow,
      purple: styles.color_purple,
      orange: styles.color_orange,
      blue: styles.color_blue,
      red: styles.color_red,
      turquoise: styles.color_turquoise,
      indigo: styles.color_indigo,
      lilac: styles.color_lilac,
      neutral: styles.color_neutral,
      inverted: styles.color_inverted,
      success: styles.color_success,
      warning: styles.color_warning,
      error: styles.color_error,
      brand_default: styles.brand_default,
      brand_blue: styles.color_brand_blue,
    },
    variant: {
      badge: styles.rectangle,
      pill: styles.pill,
      ghost: styles.ghost,
      stroke: styles.stroke,
    },
    round: {
      true: styles.round,
    },
    withIcon: {
      true: styles.with_icon,
    },
    iconOnly: {
      true: styles.icon_only,
    },
    truncate: {
      true: styles.truncate,
    },
  },
  compoundVariants: [
    {
      variant: ['badge', 'pill'],
      color: 'default',
      className: styles.bgcolor_default,
    },
    {
      variant: 'ghost',
      color: 'default',
      className: styles.textcolor_default,
    },
  ],
  defaultVariants: {
    size: 's',
    color: 'default',
    variant: 'badge',
    withIcon: false,
    round: false,
  },
});

export type ChipColor =
  | 'default'
  | 'teal'
  | 'green'
  | 'pink'
  | 'yellow'
  | 'purple'
  | 'orange'
  | 'blue'
  | 'red'
  | 'turquoise'
  | 'indigo'
  | 'lilac'
  | 'neutral'
  | 'inverted'
  | 'success'
  | 'warning'
  | 'error'
  | 'brand_default'
  | 'brand_blue';

export type ChipSize = 'xs' | 's' | 'm' | 'l';

export type ChipProps = HTMLAttributes<HTMLSpanElement> & {
  size?: ChipSize;
  variant?: 'badge' | 'pill' | 'ghost' | 'stroke';
  color?: ChipColor;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  round?: boolean;
  truncate?: boolean;
};

/**
 * Chip
 *
 * Used to display a small amount of information with various preset color options.
 *
 * Eg. Version badge, approval status indicator, badges on thumbnails.
 *
 * You can pass an <Icon /> component as the `icon` prop for display on the left side of
 * the chip. Icons used here must be of size='xs' per design specification. While this will
 * normally be an icon, you can pass any React node.
 *
 * ```tsx
 * <Chip variant="badge" size="s">Default badge</Chip>
 * ```
 *
 * ```tsx
 * <Chip variant="badge" size="s" color='purple' icon={<Icon size='xs' name='user_group'}>Editors</Chip>
 * ```
 *
 */
export const Chip = forwardRef<HTMLSpanElement, ChipProps>(
  (
    {
      size = 's',
      variant = 'badge',
      color = 'default',
      icon,
      round,
      className,
      children,
      truncate = false,
      ...props
    },
    ref,
  ) => {
    const hasChildren = Boolean(children);

    const classes = chipVariants({
      size,
      color,
      variant,
      round,
      withIcon: hasChildren && Boolean(icon),
      iconOnly: hasChildren === false && Boolean(icon),
      truncate,
      className,
    });
    return (
      <span ref={ref} {...props} className={classes}>
        {icon}
        {children ? (
          truncate ? (
            <span className={styles.content}>{children}</span>
          ) : (
            children
          )
        ) : null}
      </span>
    );
  },
);
